import React, { useState, useCallback, useEffect, useRef } from 'react';
import '../../styles/chabot/Chatbot.css';

/* ══════════════════════════════════════
   KNOWLEDGE BASE
══════════════════════════════════════ */
const KB = [
  { keys: ['what is incogniflex', 'about', 'who are you', 'company'],
    a: 'Incogniflex is an enterprise AI platform company. We specialise in AI-powered SaaS, cloud-native engineering, AI agents & orchestration, MLOps, and vertical AI solutions across healthcare, logistics, and manufacturing.' },
  { keys: ['service', 'offer', 'do you'],
    a: 'Our core services:\n  → AI-Powered SaaS & Analytics\n  → Cloud Engineering (AWS / GCP / Azure)\n  → AI Agents & Orchestration\n  → MLOps & GPU Workloads\n  → AIOps & DevOps Automation\n  → Vertical AI (healthcare, logistics, retail)' },
  { keys: ['price', 'cost', 'pricing', 'quote', 'how much'],
    a: 'Pricing is scoped per project. Factors include stack complexity, integration depth, and SLA tier.\n\n  → Visit /contact to request a tailored proposal\n  → Average enterprise engagement starts at $15k/mo\n  → Proof-of-concept engagements available' },
  { keys: ['cloud', 'migration', 'aws', 'gcp', 'azure', 'kubernetes'],
    a: 'We architect and migrate workloads to AWS, GCP, and Azure.\n\n  → Lift-and-shift + cloud-native re-architecture\n  → Kubernetes & CI/CD pipeline automation\n  → Cost optimisation: avg. 34% reduction post-migration' },
  { keys: ['ai agent', 'agent', 'automation', 'orchestration', 'workflow'],
    a: 'Our AI Agents division deploys autonomous systems that:\n\n  → Execute multi-step workflows without human input\n  → Integrate with your CRM, ERP, and data warehouses\n  → Support multi-agent orchestration at enterprise scale' },
  { keys: ['custom model', 'custom ai', 'train', 'fine-tune', 'domain'],
    a: 'Yes — we build and fine-tune domain-specific models for:\n\n  Healthcare · Logistics · Retail\n  Finance · Manufacturing · Education\n\n  → Contact us with your use case for a scoping call.' },
  { keys: ['support', '24/7', 'help', 'sla'],
    a: '24/7 support is included in all enterprise plans.\n\n  → Dedicated account manager\n  → P1 response time: < 15 minutes\n  → Support portal: support.incogniflex.ai\n  → Emergency: +1 (415) 555-0100' },
  { keys: ['contact', 'sales', 'email', 'reach', 'talk'],
    a: 'You can reach us via:\n\n  → Email: hello@incogniflex.com\n  → Sales: sales@incogniflex.ai\n  → Phone: +1 (415) 555-0100\n  → Or hit /contact in the navigation.' },
  { keys: ['security', 'compliance', 'soc', 'gdpr', 'hipaa'],
    a: 'Security is baked in at every layer:\n\n  ✓ SOC 2 Type II certified\n  ✓ GDPR compliant\n  ✓ HIPAA compliant\n  ✓ End-to-end encryption\n  ✓ Zero-trust network architecture' },
  { keys: ['hello', 'hi', 'hey', 'start', 'help'],
    a: 'Hello — I am IGFX Terminal v2.4.\nType a query or use a command below:\n\n  $ services    — list all offerings\n  $ pricing     — engagement models\n  $ contact     — reach the team\n  $ security    — compliance & certs\n  $ help        — show this menu' },
];

const COMMANDS = {
  services:  KB[1].a,
  pricing:   KB[2].a,
  contact:   KB[7].a,
  security:  KB[8].a,
  clear:     '__CLEAR__',
  help:      'Available commands:\n\n  $ services\n  $ pricing\n  $ contact\n  $ security\n  $ clear\n  $ help',
};

function findResponse(raw) {
  const msg = raw.trim().toLowerCase();
  if (!msg) return null;

  // exact command
  const cmd = msg.replace(/^\$\s*/, '');
  if (COMMANDS[cmd]) return COMMANDS[cmd];

  // KB match
  for (const entry of KB) {
    if (entry.keys.some(k => msg.includes(k))) return entry.a;
  }

  return `Query "${raw}" not recognised.\n\nTry: $ help  to see available commands,\nor ask about services / pricing / contact / security.`;
}

/* ══════════════════════════════════════
   TYPEWRITER EFFECT HOOK
══════════════════════════════════════ */
function useTypewriter(text, speed = 18) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    setDisplayed('');
    setDone(false);
    if (!text) { setDone(true); return; }
    let i = 0;
    const tick = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) { clearInterval(tick); setDone(true); }
    }, speed);
    return () => clearInterval(tick);
  }, [text, speed]);

  return [displayed, done];
}

/* ── Bot message with typewriter ── */
function BotMessage({ text, isLatest }) {
  const [typed, done] = useTypewriter(isLatest ? text : null, 14);
  const shown = isLatest ? typed : text;

  return (
    <div className="ct-msg ct-bot">
      <div className="ct-msg-meta">
        <span className="ct-prefix bot-prefix">IGFX&gt;</span>
      </div>
      <pre className="ct-text">{shown}{isLatest && !done && <span className="ct-cursor">▋</span>}</pre>
    </div>
  );
}

/* ── User message ── */
function UserMessage({ text }) {
  return (
    <div className="ct-msg ct-user">
      <div className="ct-msg-meta">
        <span className="ct-prefix user-prefix">you@igfx:~$</span>
      </div>
      <pre className="ct-text">{text}</pre>
    </div>
  );
}

/* ── Typing indicator ── */
function TypingIndicator() {
  return (
    <div className="ct-msg ct-bot ct-typing">
      <span className="ct-prefix bot-prefix">IGFX&gt;</span>
      <span className="ct-dots">
        <span /><span /><span />
      </span>
    </div>
  );
}

/* ══════════════════════════════════════
   QUICK CHIPS
══════════════════════════════════════ */
const CHIPS = ['services', 'pricing', 'security', 'contact', 'help'];

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
const ChatbotTerminal = () => {
  const [open, setOpen]       = useState(false);
  const [input, setInput]     = useState('');
  const [messages, setMessages] = useState([
    { from: 'bot', text: 'IGFX Terminal v2.4 — online.\nType $ help to see commands or ask a question.\n\nSystem status: ████████████ 100% operational', id: 0 },
  ]);
  const [typing, setTyping]   = useState(false);
  const [msgId, setMsgId]     = useState(1);
  const bodyRef               = useRef(null);
  const inputRef              = useRef(null);

  /* scroll to bottom */
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, typing]);

  /* focus input on open */
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const send = useCallback((text) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    const userMsg = { from: 'user', text: trimmed, id: msgId };
    setMsgId(n => n + 1);

    const response = findResponse(trimmed);

    if (response === '__CLEAR__') {
      setMessages([{ from: 'bot', text: 'Terminal cleared.\nType $ help to see available commands.', id: msgId + 1 }]);
      setMsgId(n => n + 2);
      return;
    }

    setMessages(prev => [...prev, userMsg]);
    setTyping(true);

    // simulate processing delay
    const delay = 400 + Math.min(trimmed.length * 8, 600);
    setTimeout(() => {
      setTyping(false);
      setMessages(prev => [...prev, {
        from: 'bot',
        text: response || "I didn't catch that. Try $ help.",
        id: msgId + 1,
      }]);
      setMsgId(n => n + 1);
    }, delay);
  }, [msgId]);

  const onSubmit = (e) => {
    e.preventDefault();
    send(input);
    setInput('');
  };

  const onChip = (chip) => {
    send(`$ ${chip}`);
  };

  const latestBotId = [...messages].reverse().find(m => m.from === 'bot')?.id;

  return (
    <>
      {/* ── FAB toggle button ── */}
      <button
        className={`ct-fab ${open ? 'ct-fab--open' : ''}`}
        onClick={() => setOpen(v => !v)}
        aria-label={open ? 'Close terminal' : 'Open AI terminal'}
      >
        <span className="fab-icon-open">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/>
          </svg>
        </span>
        <span className="fab-icon-close">✕</span>
        <span className="fab-pulse" />
      </button>

      {/* ── Terminal panel ── */}
      <div className={`ct-panel ${open ? 'ct-panel--open' : ''}`} role="dialog" aria-label="Incogniflex AI Terminal">

        {/* title bar */}
        <div className="ct-titlebar">
          <div className="ct-traffic">
            <span className="tl-dot tl-red"   onClick={() => setOpen(false)} title="Close" />
            <span className="tl-dot tl-yellow" title="Minimise" />
            <span className="tl-dot tl-green"  title="Expand" />
          </div>
          <div className="ct-title-center">
            <span className="ct-title-icon">⬡</span>
            IGFX Terminal — AI Assistant
          </div>
          <div className="ct-title-right">
            <span className="ct-status-dot" />
            <span className="ct-status-txt">online</span>
          </div>
        </div>

        {/* path bar */}
        <div className="ct-pathbar">
          <span className="path-user">incogniflex</span>
          <span className="path-sep">@</span>
          <span className="path-host">ai-terminal</span>
          <span className="path-sep">:</span>
          <span className="path-dir">~/assistant</span>
          <span className="path-branch"> [main]</span>
        </div>

        {/* message body */}
        <div className="ct-body" ref={bodyRef} id="chatbot-message-container">
          {messages.map((msg) =>
            msg.from === 'bot'
              ? <BotMessage key={msg.id} text={msg.text} isLatest={msg.id === latestBotId} />
              : <UserMessage key={msg.id} text={msg.text} />
          )}
          {typing && <TypingIndicator />}
        </div>

        {/* quick chips */}
        <div className="ct-chips">
          {CHIPS.map(c => (
            <button key={c} className="ct-chip" onClick={() => onChip(c)}>
              $ {c}
            </button>
          ))}
        </div>

        {/* input */}
        <form className="ct-input-bar" onSubmit={onSubmit}>
          <span className="ct-input-prompt">you@igfx:~$</span>
          <input
            ref={inputRef}
            className="ct-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="type a command or question…"
            autoComplete="off"
            spellCheck={false}
            aria-label="Terminal input"
          />
          <button type="submit" className="ct-send" aria-label="Send">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </form>

      </div>

      {/* backdrop on mobile */}
      {open && <div className="ct-backdrop" onClick={() => setOpen(false)} aria-hidden />}
    </>
  );
};

export default ChatbotTerminal;